import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { useContext, useMemo } from 'react';
import { CreditCardForm } from '../PaymentForms/CreditCardForm';
import { OtherPaymentForm } from '../PaymentForms/OtherPaymentForm';
import { FormByPayment } from '@/constants/FormByPayment';
import { PaypalForm } from '../PaymentForms/Paypal';
import { GooglePayForm } from '../PaymentForms/GooglePayForm';
import { LinkPayForm } from '../PaymentForms/LinkPayForm';
import { KlarnaForm } from '../PaymentForms/KlarnaForm';
import { AfterPayForm } from '../PaymentForms/AfterPayForm';
import { BlikPayForm } from '../PaymentForms/BlikPayForm';
import { P24PayForm } from '../PaymentForms/P24PayForm';
import { CashAppForm } from '../PaymentForms/CashAppForm';
import { GenericPayForm } from '../PaymentForms/GenericPayForm';
import { ApplePayForm } from '../PaymentForms/ApplePayForm';
import { StripeGenericPayForm } from '../PaymentForms/StripeGenericPayForm';
import { PaymentMethodsToStripe } from '@/constants/stripe';
import { TaxInfoPrompt } from './TaxInfoPrompt';
import { VenmoPayForm } from '../PaymentForms/VenmoPayForm';

interface PaymentMethodFormProps {
  blockedForZipCode: boolean;
  setBlockedForZipCode: (showInvalidZipMode: boolean) => void;
  setIsZipCodeValid: (isZipCodeValid: boolean) => void;
}

export const PaymentMethodForm = ({
  blockedForZipCode,
  setBlockedForZipCode,
  setIsZipCodeValid,
}: PaymentMethodFormProps) => {
  const { paymentMethodSelected, otherMethodSelected } = useContext(CheckoutContext);

  const PaymentMethodFormComponent = useMemo(() => {
    switch (paymentMethodSelected) {
      case PaymentMethodEnum.CREDIT_CARD:
        return <CreditCardForm />;
      case PaymentMethodEnum.PAYPAL:
        return <PaypalForm />;
      case PaymentMethodEnum.AMAZON_PAY:
        return <StripeGenericPayForm paymentMethod={PaymentMethodsToStripe[PaymentMethodEnum.AMAZON_PAY]} />;
      case PaymentMethodEnum.GOOGLE_PAY:
        return <GooglePayForm />;
      case PaymentMethodEnum.APPLE_PAY:
        return <ApplePayForm />;
      case PaymentMethodEnum.LINK:
        return <LinkPayForm />;
      case PaymentMethodEnum.KLARNA:
        return <KlarnaForm />;
      case PaymentMethodEnum.VENMO:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.VENMO} />;
      case PaymentMethodEnum.OTHER:
        return <OtherPaymentForm />;
      case PaymentMethodEnum.CASH_APP:
        return <CashAppForm />;
      case PaymentMethodEnum.IDEAL:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.IDEAL} />;
      case PaymentMethodEnum.AFFIRM:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.AFFIRM} />;
      case PaymentMethodEnum.BLIK:
        return <BlikPayForm />;
      case PaymentMethodEnum.AFTERPAY:
        return <AfterPayForm />;
      case PaymentMethodEnum.PIX:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.PIX} />;
      case PaymentMethodEnum.P24:
        return <P24PayForm />;
      case PaymentMethodEnum.BLIK_CHECKOUT:
        return <BlikPayForm />;
      case PaymentMethodEnum.TRUSTLY:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.TRUSTLY} />;
      case PaymentMethodEnum.EPS:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.EPS} />;
      case PaymentMethodEnum.BANCONTACT:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.BANCONTACT} />;
      case PaymentMethodEnum.ALIPAY:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.ALIPAY} />;
      case PaymentMethodEnum.UNIONPAY:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.UNIONPAY} />;
      case PaymentMethodEnum.VERIFON:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.VERIFON} />;
      case PaymentMethodEnum.NEOSURF:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.NEOSURF} />;
      case PaymentMethodEnum.NUVEI_NEOSURF:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.NUVEI_NEOSURF} />;
      case PaymentMethodEnum.PAYME:
        return <GenericPayForm paymentMethod={PaymentMethodEnum.PAYME} />;
      case PaymentMethodEnum.VENMO:
        return <VenmoPayForm />;
      default:
        return FormByPayment[otherMethodSelected as ExcludeOtherPaymentMethod];
    }
  }, [paymentMethodSelected, otherMethodSelected]);

  const handleValidate = (isValid: boolean) => {
    setIsZipCodeValid(isValid);
  };

  return (
    <div data-testid="payment-method-form" className="flex flex-col gap-4">
      {blockedForZipCode ? (
        <TaxInfoPrompt onBack={() => setBlockedForZipCode(false)} onValidate={handleValidate} />
      ) : (
        PaymentMethodFormComponent
      )}
    </div>
  );
};
